import { playSound, Sound } from '~/assets/sounds';
import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import {
	HudTaskType,
	IClientConfigs,
	IHudTaskProgress,
	numWord,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { INotificationsService } from '../NotificationsService';
import {
	CharacterOwnedDailyTasks,
	CharacterSharedDailyTasks,
	IDailyTask,
	IDailyTaskClientData,
	IDailyTasksProgress,
	IDailyTasksService,
} from './dailyTasksServiceTypes';

export class DailyTasksService implements IDailyTasksService {
	private hudTask = new Subject<IHudTaskProgress | null>(null);
	private tasksExpireAt = new Subject<number>(0);

	private ownedTasks = new Subject<(IDailyTaskClientData | null)[]>([]);
	private sharedTasks = new Subject<IDailyTaskClientData[]>([]);
	private tasks = new Subject<IDailyTask[]>([]);

	private progress = new Subject<IDailyTasksProgress>({
		completed: 0,
		limit: null,
		maxGivenTasks: null,
	});

	constructor(
		private readonly backend: BackendService,
		private readonly notifications: INotificationsService,
		private readonly gameConfig: GameConfigService,
	) {
		this.gameConfig
			.getConfigs()
			.subscribe(this.handleGameConfigUpdate.bind(this));

		this.backend.addEventHandler(
			'dailyTasks:updateOwnedTasks',
			(payload: CharacterOwnedDailyTasks) => {
				const owned = payload.tasks ?? [];

				this.ownedTasks.next(owned);
				this.updateAllTasks();
			},
		);

		this.backend.addEventHandler(
			'dailyTasks:updateSharedTasks',
			(payload: CharacterSharedDailyTasks) => {
				const shared = payload.tasks ?? [];

				this.sharedTasks.next(shared);
				this.updateAllTasks();

				if (typeof payload.expiresAt === 'number') {
					this.tasksExpireAt.next(payload.expiresAt);
				}
			},
		);

		// @NOTE На бекенде нет такого
		this.backend.addEventHandler('dailyTasks:complete', () => {
			playSound(Sound.DailyQuestComplited);
		});
	}

	public getHudTask(): IObservable<IHudTaskProgress | null> {
		return this.hudTask;
	}

	@notifyOnError
	public async setIsPinned(taskId: string, isPinned: boolean): Promise<void> {
		await this.backend.invoke('dailyTasks:setIsPinned', {
			taskId,
			isPinned,
		});
	}

	public getTasks(): IObservable<IDailyTask[]> {
		return this.tasks;
	}

	public getTasksExpireAt(): IObservable<number> {
		return this.tasksExpireAt;
	}

	public getDailyTasksProgress(): IObservable<IDailyTasksProgress> {
		return this.progress;
	}

	public getPinnedTaskId(): string | null {
		const tasks = this.tasks.getValue();
		const pinnedTask = tasks.find((t) => t.isPinned);
		return pinnedTask?.id ?? null;
	}

	private handleGameConfigUpdate(configs: Partial<IClientConfigs>): void {
		if (!configs) {
			return;
		}

		this.refreshProgress();
	}

	private refreshProgress(): void {
		const taskList = this.tasks.getValue();
		const config = this.gameConfig.configs?.dailyTasks;

		const limit = config?.maxDailyCompleteTasks ?? null;
		const maxGivenTasks = config?.maxGivenTasks ?? null;

		const completed = taskList.filter(
			(t) => t.currentCount >= t.targetCount,
		).length;

		this.progress.next({ completed, limit, maxGivenTasks });
	}

	private updateAllTasks() {
		const ownedTasks = this.ownedTasks.getValue();
		const sharedTasks = this.sharedTasks.getValue();

		if (!ownedTasks?.length && !sharedTasks?.length) {
			this.tasks.next([]);
			this.hudTask.next(null);
			this.refreshProgress();

			return;
		}

		const maxLength = Math.max(
			ownedTasks?.length ?? 0,
			sharedTasks?.length ?? 0,
		);

		const fullTasks = Array.from({ length: maxLength }, (_, index) => {
			const ownedTask = ownedTasks?.[index] ?? null;
			const sharedTask =
				index < (sharedTasks?.length ?? 0) ? sharedTasks[index] : null;
			const baseTask = ownedTask ?? sharedTask;

			if (!baseTask) {
				return null;
			}

			const taskConfig = this.getTaskConfig(baseTask.id);

			let description = taskConfig?.text ?? '???';
			if (taskConfig?.wordForms?.length === 3) {
				const word = numWord(taskConfig.targetCount, taskConfig.wordForms);
				description = description
					.replace('{count}', taskConfig.targetCount.toString())
					.replace('{word}', word);
			}

			const task: IDailyTask = {
				id: baseTask.id,
				name: taskConfig?.title ?? '???',
				description,
				currentCount: baseTask.progress ?? 0,
				targetCount: taskConfig?.targetCount ?? 1,
				rewardMoney: taskConfig?.rewardMoney ?? 0,
				rewardExp: taskConfig?.rewardExp ?? 0,
				isPinned: baseTask.isPinned,
			};

			return task;
		}).filter((task): task is IDailyTask => task !== null);

		this.tasks.next(fullTasks);

		this.updateHudTaskFromTasks(fullTasks);
		this.refreshProgress();
	}

	private getTaskConfig(taskId: string) {
		const dailyTasksConfig = this.gameConfig.configs?.dailyTasks;
		if (!dailyTasksConfig) {
			return null;
		}

		const task =
			dailyTasksConfig.tasks.find((t) => t.taskId === taskId) ?? null;
		if (!task) {
			return null;
		}

		const targetIdByTaskId = dailyTasksConfig.targetIdByTaskId;

		const targetId = targetIdByTaskId?.[taskId];
		if (!targetId) {
			return {
				...task,
				title: '???',
				text: '???',
				wordForms: null,
			};
		}

		const target =
			dailyTasksConfig.targets?.find((t) => t.id === targetId) ?? null;

		return {
			...task,
			title: target?.title ?? taskId,
			text: target?.text ?? taskId,
			wordForms: target?.wordForms ?? null,
		};
	}

	private updateHudTaskFromTasks(tasks: IDailyTask[]) {
		const pinnedTask = tasks.find((t) => t.isPinned);

		if (pinnedTask && pinnedTask.currentCount < pinnedTask.targetCount) {
			this.hudTask.next({
				type: HudTaskType.DAILY,
				text: pinnedTask.description,
				targetCount: pinnedTask.targetCount,
				currentCount: pinnedTask.currentCount,
			});
		} else {
			this.hudTask.next(null);
		}
	}
}
